import { ChakraProvider } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { useDailyRoom } from '@web-app/components/CallV2/hooks/useDailyRoom';
import { PatientCallLayout } from '@web-app/components/CallV2/PatientCallLayout/PatientCallLayout';
import { ProviderCallLayout } from '@web-app/components/CallV2/ProviderCallLayout/ProviderCallLayout';
import { PreCallLandingPage } from '@web-app/components/PreCallLandingPage/PreCallLandingPage';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { CallState } from '@web-app/hocs/daily/types';
import { useAppointmentByRoomId } from '@web-app/hooks/queries/appointment-by-room-name';
import { useGetUserOrganizationData } from '@web-app/hooks/queries/user';
import { useEnsureProviderAuth } from '@web-app/hooks/useEnsureProviderAuth';
import { callV2Theme } from '@web-app/theming/theme';
import {
  getOrganizationConfig,
  getWelcomeCompanyName,
} from '@web-app/utils/api/whitelabel/organization-configs';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import { useSyncAppointmentTypeIdAttribute } from '@web-app/utils/feature-flags/prescriber-flow';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface CallPageRootV2 {
  predefinedCallState?: CallState;
}

/**
 * V2 of the in-session experience using Daily Prebuilt UI (iFrame).
 * This component provides a simplified video call experience using Daily's built-in UI.
 * Renders different layouts for patients (full-screen) and providers (split-screen with panels).
 */
export const CallPageRootV2: FC<CallPageRootV2> = () => {
  const [isCallOn, setIsCallOn] = useState(false);

  const { roomId } = useDailyRoom();
  const { data: appointment } = useAppointmentByRoomId(roomId);
  const { error, user } = useEnsureProviderAuth({ redirectTo: `/${roomId}` });
  const { trackEvent } = useAnalytics();
  const { data } = useGetUserOrganizationData();

  const appointmentTypeId = useMemo(
    () =>
      appointment?.healthie?.appointment_type?.id ??
      appointment?.appointmentType?.id,
    [appointment],
  );
  useSyncAppointmentTypeIdAttribute(appointmentTypeId);

  const hasLoggedPageOpen = useRef(false);
  useEffect(() => {
    if (roomId && appointment && !user && !hasLoggedPageOpen.current) {
      hasLoggedPageOpen.current = true;
      datadogLogs.logger.info(
        'Patient opened call page',
        callLogContext('call-join', {
          roomId,
          participantType: 'patient',
          clientId: appointment.client_id,
          providerId: appointment.provider_id,
          healthieAppointmentId: appointment.healthie_id,
          timestamp: new Date().toISOString(),
        }),
      );
    }
  }, [roomId, appointment, user]);

  const handleClientJoinCall = useCallback(() => {
    // Join call immediately - don't let analytics block the UI
    setIsCallOn(true);

    // Defer analytics to next tick to ensure UI updates first
    // This prevents ad blockers from causing timeouts that block joining
    setTimeout(() => {
      trackEvent(EVENT_NAMES.JOIN_SESSION);

      const {
        client_id: clientId,
        provider_id: providerId,
        healthie_id: healthieAppointmentId,
      } = appointment || {};

      datadogLogs.logger.info(
        'Patient pressed join call',
        callLogContext('call-join', {
          roomId: roomId as string,
          participantType: 'patient',
          clientId,
          providerId,
          healthieAppointmentId,
          timestamp: new Date().toISOString(),
        }),
      );
    }, 0);
  }, [trackEvent, roomId, appointment]);

  const handleProviderJoinCall = useCallback(() => {
    trackEvent(EVENT_NAMES.PROVIDER_JOIN_SESSION);

    const isLocalOrTest =
      process.env.NODE_ENV === 'development' ||
      window.location.hostname === 'localhost';

    if (isLocalOrTest) {
      window.location.href = `/sign-in?redirect_url=${encodeURIComponent(
        window.location.href,
      )}`;
    } else {
      const orgConfig = getOrganizationConfig(data?.organizationId);
      const signInUrl = orgConfig.app.signInRedirectUrl;
      window.location.href = `${signInUrl}?redirect_url=${encodeURIComponent(
        window.location.href,
      )}`;
    }
  }, [data?.organizationId, trackEvent]);

  // Wrap all content with light theme
  return (
    <ChakraProvider theme={callV2Theme}>
      {!roomId ? (
        <div>Loading...</div>
      ) : !error && (isCallOn || user) ? (
        // Show call interface if patient joined or provider is authenticated
        user ? (
          // Provider view: split-screen with panels
          <ProviderCallLayout userName={user.fullName || ''} />
        ) : (
          // Patient view: full-screen video
          <PatientCallLayout />
        )
      ) : (
        // Pre-call landing page
        appointment && (
          <PreCallLandingPage
            organizationId={data?.organizationId}
            welcomeCompanyName={getWelcomeCompanyName(data?.organizationId)}
            onClientJoinCall={handleClientJoinCall}
            onProviderJoinCall={handleProviderJoinCall}
          />
        )
      )}
    </ChakraProvider>
  );
};
